import React, {useEffect, useRef, useState} from 'react';
import {Image as RNImage, Platform, View, StyleSheet, TouchableOpacity} from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import Svg, {Rect, Text as SvgText, Image as SvgImage} from 'react-native-svg';
import {width, height, totalSize} from 'react-native-dimension';

import {colorPalette, colors} from '../../../core/config/color.config';
import {RNEText, SVGIcon, IconName} from '../../components';
import HouseCallIcon from '../../../assets/logo/house-call-icon.png';

interface ShareDoctorProfilePanelProps {
  shareUrl: string;
  doctorName?: string;
  onClose?: () => void;
  onDownloadQr: () => void;
  onShareQr: () => void;
  getExportRef: (ref: any) => void;
}

const QR_SIZE = Platform.OS === 'web' ? 220 : totalSize(28);
const QR_LOGO_SIZE = QR_SIZE * 0.22;

// Fixed pixel dimensions for the exported/downloaded card - this is a file
// that gets saved and shared outside the app, so its size should be
// consistent regardless of the viewing device, unlike the on-screen QR
// above which scales with totalSize().
const CARD_WIDTH = 360;
const CARD_HEIGHT = 460;
const CARD_QR_SIZE = 240;
const CARD_QR_LOGO_SIZE = CARD_QR_SIZE * 0.22;
const CARD_WHITE_PANEL_SIZE = CARD_QR_SIZE + 32;
const CARD_WHITE_PANEL_X = (CARD_WIDTH - CARD_WHITE_PANEL_SIZE) / 2;
const CARD_WHITE_PANEL_Y = 130;

interface ActionButtonProps {
  title: string;
  iconName: IconName;
  outlined?: boolean;
  onPress: () => void;
}

// A plain, explicitly-laid-out icon+label button - see git history/PR notes:
// RNEButton's icon+iconPosition props don't reliably render a visible title
// on native, so this sidesteps that with a plain row (icon + text).
const ActionButton = ({title, iconName, outlined, onPress}: ActionButtonProps) => (
  <TouchableOpacity
    onPress={onPress}
    activeOpacity={0.8}
    style={[styles.actionButton, outlined ? styles.actionButtonOutlined : styles.actionButtonFilled]}>
    <SVGIcon name={iconName} size={totalSize(1.6)} />
    <RNEText
      label={title}
      style={[styles.actionButtonText, outlined ? styles.actionButtonTextOutlined : styles.actionButtonTextFilled]}
    />
  </TouchableOpacity>
);

/**
 * Pure presentational panel shared by the web and native Share modal
 * containers (ShareDoctorProfileModal.tsx / .native.tsx). Deliberately has
 * no dependency on any package that only works on one platform (Clipboard,
 * react-native-share, camera-roll, etc. all internally use TurboModuleRegistry,
 * which react-native-web doesn't implement - importing them anywhere reachable
 * from the web bundle breaks the web build even behind a Platform.OS check,
 * since bundlers resolve the whole import graph statically). Those live only
 * in the platform-specific containers, passed in here as plain callbacks.
 *
 * Only offers the QR code, not a copyable/shareable text link - see the note
 * below for why.
 */
const ShareDoctorProfilePanel = ({
  shareUrl,
  doctorName,
  onClose,
  onDownloadQr,
  onShareQr,
  getExportRef,
}: ShareDoctorProfilePanelProps) => {
  const onScreenQrRef = useRef<any>(null);
  const [qrDataUri, setQrDataUri] = useState<string | null>(null);
  const [logoReady, setLogoReady] = useState(false);

  // The QR's center logo loads asynchronously (it's an <Image>, decoded off
  // the main paint pass). Capturing the QR via toDataURL() before that
  // finishes catches it mid-render, with the logo's reserved space still
  // blank - that's what "downloaded QR has no logo" turned out to be.
  // Preloading it first (and only handing `logo` to <QRCode> once it
  // resolves) makes the wait deterministic instead of guessing a delay.
  useEffect(() => {
    // react-native-web resolves a required PNG straight to a string URL at
    // build time (no resolveAssetSource - react-native-web doesn't
    // implement it at all); native gives an opaque numeric asset id that
    // needs resolveAssetSource to get a real URI first.
    const uri =
      typeof HouseCallIcon === 'string'
        ? HouseCallIcon
        : RNImage.resolveAssetSource?.(HouseCallIcon)?.uri;
    if (!uri) {
      setLogoReady(true);
      return;
    }
    RNImage.prefetch(uri)
      .then(() => setLogoReady(true))
      .catch(() => setLogoReady(true));
  }, []);

  // Render the on-screen QR once its logo is ready, then snapshot it as a
  // plain PNG so it can be embedded as an <Image> inside the branded export
  // card below - two separate <Svg> roots (rather than nesting one inside
  // the other) since react-native-svg's native renderers expect one root
  // Svg per native view. The extra delay here is just for the logo's own
  // React re-render/paint after logoReady flips true (its bytes are already
  // preloaded above, so this is a normal frame, not a network/decode wait).
  useEffect(() => {
    if (!logoReady) return;
    setQrDataUri(null);
    const timer = setTimeout(() => {
      onScreenQrRef.current?.toDataURL((base64: string) => {
        setQrDataUri(`data:image/png;base64,${base64}`);
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [shareUrl, logoReady]);

  return (
    <View style={styles.root}>
      <View style={styles.header}>
        <RNEText
          label="Share Doctor Profile"
          variant="subheader"
          weight="600"
          color="accent"
        />
        {!!onClose && (
          <TouchableOpacity onPress={onClose} hitSlop={{top: 12, bottom: 12, left: 12, right: 12}}>
            <SVGIcon name="close" size={totalSize(1.8)} />
          </TouchableOpacity>
        )}
      </View>

      <View style={styles.noteContainer}>
        <SVGIcon name="information" size={totalSize(1.6)} />
        <RNEText
          label="Links shared through apps like Messenger or Microsoft Teams may open in their own in-app browser instead of this app. Scanning the QR code is a more direct way to open this profile."
          variant="body"
          color="grey"
          style={styles.noteText}
        />
      </View>

      <View style={styles.panel}>
        <View style={styles.brandedCardOnScreen}>
          <RNEText label="HouseCall" style={styles.brandWordmark} />
          {!!doctorName && <RNEText label={doctorName} style={styles.brandDoctorName} />}
          <View style={styles.qrWrapper} collapsable={false}>
            <QRCode
              value={shareUrl}
              size={QR_SIZE}
              backgroundColor={colorPalette.white}
              ecl="H"
              logo={logoReady ? HouseCallIcon : undefined}
              logoSize={QR_LOGO_SIZE}
              logoBackgroundColor={colorPalette.white}
              logoBorderRadius={QR_LOGO_SIZE / 4}
              logoMargin={2}
              getRef={ref => {
                onScreenQrRef.current = ref;
              }}
            />
          </View>
        </View>

        <View style={styles.actionsRow}>
          <ActionButton title="Download" iconName="download-down" outlined onPress={onDownloadQr} />
          <ActionButton title="Share" iconName="share" onPress={onShareQr} />
        </View>
      </View>

      {/* Hidden branded export card: same content as the on-screen card
          above, composed as a single flat Svg so it can be captured as one
          PNG via toDataURL for Download/Share. Not meant to be seen directly. */}
      <View style={styles.exportCardHost} pointerEvents="none">
        <Svg
          width={CARD_WIDTH}
          height={CARD_HEIGHT}
          ref={ref => getExportRef(ref)}>
          <Rect x={0} y={0} width={CARD_WIDTH} height={CARD_HEIGHT} rx={28} fill={colorPalette.purple} />
          <SvgText x={CARD_WIDTH / 2} y={56} fontSize={26} fontWeight="bold" fill={colorPalette.white} textAnchor="middle">
            HouseCall
          </SvgText>
          {!!doctorName && (
            <SvgText x={CARD_WIDTH / 2} y={86} fontSize={15} fill={colorPalette.white} textAnchor="middle">
              {doctorName}
            </SvgText>
          )}
          <Rect
            x={CARD_WHITE_PANEL_X}
            y={CARD_WHITE_PANEL_Y}
            width={CARD_WHITE_PANEL_SIZE}
            height={CARD_WHITE_PANEL_SIZE}
            rx={20}
            fill={colorPalette.white}
          />
          {!!qrDataUri && (
            <SvgImage
              x={(CARD_WIDTH - CARD_QR_SIZE) / 2}
              y={CARD_WHITE_PANEL_Y + (CARD_WHITE_PANEL_SIZE - CARD_QR_SIZE) / 2}
              width={CARD_QR_SIZE}
              height={CARD_QR_SIZE}
              href={qrDataUri}
            />
          )}
          <SvgText
            x={CARD_WIDTH / 2}
            y={CARD_HEIGHT - 30}
            fontSize={13}
            fill={colorPalette.white}
            textAnchor="middle">
            Scan to view this profile in the HouseCall app
          </SvgText>
        </Svg>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    width: Platform.OS === 'web' ? 420 : width(85),
    paddingHorizontal: Platform.OS === 'web' ? 24 : totalSize(2),
    paddingVertical: Platform.OS === 'web' ? 24 : totalSize(2.5),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: height(1.5),
  },
  noteContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: width(1),
    backgroundColor: colors.gray.lighter,
    borderRadius: totalSize(0.8),
    padding: totalSize(1.2),
    marginBottom: height(2),
  },
  noteText: {
    flex: 1,
    fontSize: totalSize(0.9),
  },
  panel: {
    alignItems: 'center',
  },
  brandedCardOnScreen: {
    width: '100%',
    alignItems: 'center',
    backgroundColor: colorPalette.purple,
    borderRadius: totalSize(1.4),
    paddingVertical: totalSize(2),
    marginBottom: height(2),
  },
  brandWordmark: {
    fontSize: totalSize(1.6),
    fontFamily: 'Quicksand-Bold',
    color: colorPalette.white,
  },
  brandDoctorName: {
    fontSize: totalSize(1),
    fontFamily: 'Quicksand-Medium',
    color: colorPalette.white,
    marginTop: totalSize(0.3),
    marginBottom: totalSize(1.5),
  },
  qrWrapper: {
    padding: totalSize(1.5),
    borderRadius: totalSize(1),
    backgroundColor: colorPalette.white,
    marginTop: totalSize(1),
  },
  exportCardHost: {
    position: 'absolute',
    opacity: 0,
    width: 1,
    height: 1,
    overflow: 'hidden',
  },
  actionsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: width(2),
    width: '100%',
  },
  actionButton: {
    flex: 1,
    maxWidth: width(35),
    height: 48,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: width(1),
    borderRadius: 100,
    paddingHorizontal: totalSize(1.5),
  },
  actionButtonFilled: {
    backgroundColor: colorPalette.secondary,
  },
  actionButtonOutlined: {
    backgroundColor: 'transparent',
    borderWidth: width(0.4),
    borderColor: colorPalette.secondary,
  },
  actionButtonText: {
    fontFamily: 'Quicksand-Bold',
    fontSize: Platform.OS === 'web' ? 14 : totalSize(1.3),
    fontWeight: 'bold',
  },
  actionButtonTextFilled: {
    color: colorPalette.white,
  },
  actionButtonTextOutlined: {
    color: colorPalette.secondary,
  },
});

export default ShareDoctorProfilePanel;
